import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MdClose,
  MdOutlineArrowBack,
  MdOutlineCreate,
  MdOutlineFilterAlt,
} from 'react-icons/md';
import AppChip from '../../../../shared/components/app-chip/AppChip';
import AppIconButton from '../../../../shared/components/app-icon-button/AppIconButton';
import { isMobileWidth } from '../../../../shared/helper-utils/appUtils';
import CruiseLayout from '../../components/common/CruiseLayout';
import CruiseCard from '../../components/CruiseCard/CruiseCard';
import CruiseFilters, {
  buildFacets,
} from '../../components/CruiseFilters/CruiseFilters';
import CruiseSearchForm from '../../components/CruiseSearchForm/CruiseSearchForm';
import { searchCruisesApi } from '../../api/cruiseApi';
import { CRUISE_ROUTES, SORT_OPTIONS } from '../../constants/cruiseConstants';
import {
  EMPTY_FILTERS,
  applyFilters,
  buildSearchQuery,
  cheapestFare,
  cruiseKey,
  guestsSummary,
  monthLabel,
  parseSearchQuery,
  plural,
  sortCruises,
  titleCase,
  toSearchRequest,
} from '../../utils/cruiseUtils';

/**
 * Results for the search in the URL. Every visit (new search, modify
 * search, Back from details, refresh) fetches fresh results; nothing is
 * cached in storage.
 */
function CruiseResultsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const mobile = isMobileWidth();
  const search = useMemo(
    () => parseSearchQuery(location.search),
    [location.search],
  );
  const query = buildSearchQuery(search);

  const [cruises, setCruises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fareSel, setFareSel] = useState({});
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState('price');
  const [filtersOpen, setFiltersOpen] = useState(!mobile);
  const [modifyOpen, setModifyOpen] = useState(false);

  useEffect(() => {
    if (!search.destinationCode) {
      navigate(CRUISE_ROUTES.LANDING, { replace: true });
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setCruises([]);
    setFilters(EMPTY_FILTERS);
    setFareSel({});
    searchCruisesApi(toSearchRequest(search), controller.signal).then(
      (read) => {
        if (controller.signal.aborted) return;
        const list = read.ok && Array.isArray(read.data) ? read.data : [];
        setCruises(list);
        if (!read.ok) {
          setError(
            read.network
              ? 'Could not reach the server. Please try again.'
              : read.message || 'Search failed. Please try again.',
          );
        }
        setLoading(false);
      },
    );
    return () => controller.abort();
  }, [query]);

  const fareFor = (cruise) => {
    const options = cruise.fareOptions || [];
    const chosen = fareSel[cruiseKey(cruise)];
    return options.find((o) => o.fareType === chosen) || cheapestFare(cruise);
  };

  const facets = useMemo(() => buildFacets(cruises), [cruises]);
  const visible = useMemo(
    () => sortCruises(applyFilters(cruises, filters), sort, fareFor),
    [cruises, filters, sort, fareSel],
  );

  const handleSelect = (cruise) => {
    const fare = fareFor(cruise);
    if (!fare) return;
    navigate(CRUISE_ROUTES.DETAILS, { state: { search, cruise, fare } });
  };

  const openModify = () => {
    if (mobile) setModifyOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const searchTitle =
    titleCase(search.destinationName) || search.destinationCode;

  const header = mobile ? (
    <div className="cr-mobile-head">
      <div className="cr-mobile-head__bar">
        <AppIconButton
          className="white-color"
          variant="transparent"
          onClick={() => navigate(CRUISE_ROUTES.LANDING)}
        >
          <MdOutlineArrowBack />
        </AppIconButton>
        <div className="cr-mobile-head__text">
          <span>Cruises · {searchTitle}</span>
          <small>
            {monthLabel(search.month)} · {guestsSummary(search)}
            {search.wheelchairAccessible ? ' · Wheelchair access' : ''}
          </small>
        </div>
        <AppIconButton
          className="white-color"
          variant="transparent"
          onClick={() => setModifyOpen((o) => !o)}
        >
          {modifyOpen ? (
            <MdClose className="fs-4" />
          ) : (
            <MdOutlineCreate className="fs-4" />
          )}
        </AppIconButton>
        <AppIconButton
          className="white-color"
          variant="transparent"
          onClick={() => setFiltersOpen((o) => !o)}
        >
          <MdOutlineFilterAlt className="fs-4" />
        </AppIconButton>
      </div>
      {modifyOpen && (
        <CruiseSearchForm
          key={query}
          initialSearch={search}
          variant="modify"
          onSearched={() => setModifyOpen(false)}
        />
      )}
    </div>
  ) : (
    <div className="cr-modify">
      <div className="cr-modify__inner">
        <CruiseSearchForm key={query} initialSearch={search} variant="modify" />
      </div>
    </div>
  );

  return (
    <CruiseLayout step="results" loading={loading} header={header}>
      <div className="cr-split">
        {(filtersOpen || !mobile) && (
          <CruiseFilters
            facets={facets}
            filters={filters}
            onChange={setFilters}
            onClear={() => setFilters(EMPTY_FILTERS)}
            open={filtersOpen}
            onToggle={() => setFiltersOpen((o) => !o)}
          />
        )}

        <section className="cr-split__main">
          <div className="cr-results-head">
            <div>
              <b className="cr-results-head__count">
                {plural(visible.length, 'cruise')}
              </b>
              <span className="cr-muted">
                {' '}
                · prices for {guestsSummary(search)}, whole party
              </span>
            </div>
            <div className="cr-chips">
              {SORT_OPTIONS.map((o) => (
                <AppChip
                  key={o.key}
                  name="sort"
                  value={o.key}
                  chipText={o.label}
                  selected={sort === o.key}
                  className="cr-chip"
                  onClick={() => setSort(o.key)}
                />
              ))}
            </div>
          </div>

          {!loading && error && (
            <div className="cr-empty">
              {error}{' '}
              <button type="button" className="cr-link" onClick={openModify}>
                Modify search
              </button>
            </div>
          )}

          {!loading && !error && cruises.length === 0 && (
            <div className="cr-empty">
              No cruises found for these dates.{' '}
              <button type="button" className="cr-link" onClick={openModify}>
                Modify search
              </button>
            </div>
          )}

          {!loading && cruises.length > 0 && visible.length === 0 && (
            <div className="cr-empty">
              No cruises match these filters.{' '}
              <button
                type="button"
                className="cr-link"
                onClick={() => setFilters(EMPTY_FILTERS)}
              >
                Clear filters
              </button>
            </div>
          )}

          {visible.map((cruise) => (
            <CruiseCard
              key={cruiseKey(cruise)}
              cruise={cruise}
              fare={fareFor(cruise)}
              onFareChange={(fareType) =>
                setFareSel((s) => ({ ...s, [cruiseKey(cruise)]: fareType }))
              }
              onSelect={() => handleSelect(cruise)}
            />
          ))}
        </section>
      </div>
    </CruiseLayout>
  );
}

export default CruiseResultsPage;
